import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// shadcn UI components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../components/ui/use-toast';

// icons
import { ArrowLeft, Save2 } from 'iconsax-react';

// project imports
import { getPlans, createPlan, updatePlan } from '../../services/subscriptionService';

const initialFormState = {
  planCode: '',
  planName: '',
  description: '',
  price: {
    monthly: 0,
    annual: 0,
    currency: 'INR'
  },
  isActive: true,
  isCustom: true,
  limits: {
    maxAdmins: 2,
    maxCaregivers: 10,
    maxPatients: 20,
    maxFileSizeMB: 5,
    totalStorageGB: 5,
    allowedFileTypes: ['application/pdf', 'image/jpeg', 'image/png'],
    auditRetentionDays: 30
  },
  features: {
    mobileAccess: 'readonly',
    billingModule: false,
    smsNotifications: false,
    emailNotifications: true,
    pushNotifications: true,
    apiAccess: 'none',
    webhookSupport: false,
    customRoles: false,
    aiScheduling: false,
    whiteLabelBranding: false,
    offlineMobileSync: false,
    advancedReporting: false,
    payrollIntegration: false,
    multiLocation: false
  },
  support: {
    channel: 'email',
    slaHours: 48
  }
};

const PlanEdit = () => {
  const navigate = useNavigate();
  const { planId } = useParams();
  const { toast } = useToast();
  
  const isEditMode = planId && planId !== 'new';
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    if (isEditMode) {
      fetchPlanDetails();
    }
  }, [planId]);

  const fetchPlanDetails = async () => {
    try {
      setLoading(true);
      const res = await getPlans();
      const plan = res.data.find(p => p._id === planId);
      if (plan) {
        setFormData({
          ...initialFormState,
          ...plan,
          price: { ...initialFormState.price, ...(plan.price || {}) },
          limits: { ...initialFormState.limits, ...(plan.limits || {}) },
          features: { ...initialFormState.features, ...(plan.features || {}) },
          support: { ...initialFormState.support, ...(plan.support || {}) }
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Plan not found in catalog."
        });
        navigate('/subscriptions/plans');
      }
    } catch (error) {
      console.error('Error fetching plan details:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load plan details."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      const keys = path.split('.');
      if (keys.length === 1) {
        return { ...prev, [keys[0]]: value };
      } else if (keys.length === 2) {
        return {
          ...prev,
          [keys[0]]: {
            ...prev[keys[0]],
            [keys[1]]: value
          }
        };
      }
      return prev;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Simple validations
      if (!formData.planName.trim()) {
        toast({ variant: "destructive", title: "Validation Error", description: "Plan name is required." });
        return;
      }
      if (!isEditMode && !formData.planCode.trim()) {
        toast({ variant: "destructive", title: "Validation Error", description: "Plan code is required." });
        return;
      }
      if (formData.price.monthly === '' || formData.price.annual === '') {
        toast({ variant: "destructive", title: "Validation Error", description: "Price values cannot be empty." });
        return;
      }
      if (formData.limits.maxFileSizeMB === '' || formData.limits.maxFileSizeMB < 1) {
        toast({ variant: "destructive", title: "Validation Error", description: "Max file size must be at least 1 MB." });
        return;
      }
      if (formData.limits.maxCaregivers === '' || formData.limits.maxPatients === '' || formData.limits.maxAdmins === '' || formData.limits.totalStorageGB === '') {
        toast({ variant: "destructive", title: "Validation Error", description: "Limit values cannot be empty." });
        return;
      }

      if (isEditMode) {
        await updatePlan(planId, formData);
        toast({ title: "Success", description: "Subscription plan updated successfully." });
      } else {
        await createPlan(formData);
        toast({ title: "Success", description: "Subscription plan created successfully." });
      }
      
      navigate('/subscriptions/plans');
    } catch (error) {
      console.error('Save plan error:', error);
      toast({
        variant: "destructive",
        title: "Error Saving Plan",
        description: error.response?.data?.message || "Internal server error occurred."
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="outline" 
          size="icon" 
          onClick={() => navigate('/subscriptions/plans')}
          className="rounded-full"
        >
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            {isEditMode ? `Edit Plan: ${formData.planName}` : 'Create New Plan'}
          </h1>
          <p className="text-sm text-muted-foreground">
            Configure pricing model, usage counters limits, and feature toggles for this tier
          </p>
        </div>
      </div>

      <form onSubmit={handleSave}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="general">General & Pricing</TabsTrigger>
            <TabsTrigger value="limits">Resource Quotas</TabsTrigger>
            <TabsTrigger value="features">Features Toggles</TabsTrigger>
            <TabsTrigger value="support">SLA & Support</TabsTrigger>
          </TabsList>

          {/* TAB 1: GENERAL & PRICING */}
          <TabsContent value="general">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Plan General Details</CardTitle>
                <CardDescription>Name, unique code identifier, and monthly/annual pricing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="planName">Plan Name</Label>
                    <Input 
                      id="planName" 
                      placeholder="e.g. Mid-Level Growth" 
                      value={formData.planName}
                      onChange={(e) => handleInputChange('planName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="planCode">Unique Plan Code</Label>
                    <Input 
                      id="planCode" 
                      placeholder="e.g. midlevel" 
                      value={formData.planCode}
                      onChange={(e) => handleInputChange('planCode', e.target.value)}
                      disabled={isEditMode}
                      required
                    />
                    <p className="text-xs text-muted-foreground">System-level identifier. Immutable once saved.</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Plan Description</Label>
                  <Textarea 
                    id="description" 
                    placeholder="Provide a clear description of the customer segment for this tier..." 
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="monthlyPrice">Monthly Price (₹)</Label>
                    <Input 
                      id="monthlyPrice" 
                      type="number"
                      min="0"
                      value={formData.price.monthly}
                      onChange={(e) => handleInputChange('price.monthly', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="annualPrice">Annual Price (₹)</Label>
                    <Input 
                      id="annualPrice" 
                      type="number"
                      min="0"
                      value={formData.price.annual}
                      onChange={(e) => handleInputChange('price.annual', e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency Code</Label>
                    <Select 
                      value={formData.price.currency} 
                      onValueChange={(val) => handleInputChange('price.currency', val)}
                    >
                      <SelectTrigger id="currency">
                        <SelectValue placeholder="Select Currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="INR">INR (₹)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-8 border-t pt-4">
                  <div className="flex items-center space-x-2">
                    <Switch 
                      id="isActive" 
                      checked={formData.isActive}
                      onCheckedChange={(val) => handleInputChange('isActive', val)}
                    />
                    <Label htmlFor="isActive">Make Plan Available for Subscription</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch 
                      id="isCustom" 
                      checked={formData.isCustom}
                      onCheckedChange={(val) => handleInputChange('isCustom', val)}
                    />
                    <Label htmlFor="isCustom">This is a custom contract plan</Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 2: LIMITS & QUOTAS */}
          <TabsContent value="limits">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Usage Limits & Boundaries</CardTitle>
                <CardDescription>Specify maximum record limits. Use -1 to define as unlimited</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="maxCaregivers">Max Caregiver Profiles</Label>
                    <Input 
                      id="maxCaregivers" 
                      type="number"
                      min="-1"
                      value={formData.limits.maxCaregivers}
                      onChange={(e) => handleInputChange('limits.maxCaregivers', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                      required
                    />
                    <p className="text-xs text-muted-foreground">e.g. 10. -1 for unlimited.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxPatients">Max Patient Profiles</Label>
                    <Input 
                      id="maxPatients" 
                      type="number"
                      min="-1"
                      value={formData.limits.maxPatients}
                      onChange={(e) => handleInputChange('limits.maxPatients', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                      required
                    />
                    <p className="text-xs text-muted-foreground">e.g. 50. -1 for unlimited.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxAdmins">Max Administrator Logins</Label>
                    <Input 
                      id="maxAdmins" 
                      type="number"
                      min="-1"
                      value={formData.limits.maxAdmins}
                      onChange={(e) => handleInputChange('limits.maxAdmins', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                      required
                    />
                    <p className="text-xs text-muted-foreground">e.g. 2. -1 for unlimited.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="maxFileSizeMB">Max Single File Size (MB)</Label>
                    <Input 
                      id="maxFileSizeMB" 
                      type="number"
                      min="1"
                      value={formData.limits.maxFileSizeMB}
                      onChange={(e) => handleInputChange('limits.maxFileSizeMB', e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 5))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="totalStorageGB">Total File Storage Limit (GB)</Label>
                    <Input 
                      id="totalStorageGB" 
                      type="number"
                      min="-1"
                      value={formData.limits.totalStorageGB}
                      onChange={(e) => handleInputChange('limits.totalStorageGB', e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 5))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="auditRetentionDays">Audit Retention Trail (Days)</Label>
                    <Input 
                      id="auditRetentionDays" 
                      type="number"
                      min="1"
                      value={formData.limits.auditRetentionDays}
                      onChange={(e) => handleInputChange('limits.auditRetentionDays', e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 30))}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2 border-t pt-4">
                  <Label>Allowed Mime/File Types</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                    {[
                      { label: 'PDF Documents', value: 'application/pdf' },
                      { label: 'JPEG Images', value: 'image/jpeg' },
                      { label: 'PNG Images', value: 'image/png' },
                      { label: 'Word (DOCX)', value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
                      { label: 'Excel (XLSX)', value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
                      { label: 'Allow All (*)', value: '*' }
                    ].map(type => {
                      const isChecked = formData.limits.allowedFileTypes.includes(type.value);
                      return (
                        <div key={type.value} className="flex items-center space-x-2 p-2 border rounded-lg bg-card">
                          <input 
                            type="checkbox"
                            id={`mime-${type.value}`}
                            checked={isChecked}
                            onChange={(e) => {
                              let list = [...formData.limits.allowedFileTypes];
                              if (e.target.checked) {
                                if (type.value === '*') list = ['*'];
                                else {
                                  list = list.filter(item => item !== '*');
                                  list.push(type.value);
                                }
                              } else {
                                list = list.filter(item => item !== type.value);
                              }
                              handleInputChange('limits.allowedFileTypes', list);
                            }}
                            className="rounded text-primary focus:ring-primary h-4 w-4"
                          />
                          <Label htmlFor={`mime-${type.value}`} className="cursor-pointer text-xs font-normal">
                            {type.label}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 3: FEATURES TOGGLES */}
          <TabsContent value="features">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Feature Access & Capabilities</CardTitle>
                <CardDescription>Enable or disable major software modules for organizations in this tier</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 border rounded-xl bg-card">
                    <div className="space-y-0.5">
                      <Label htmlFor="billingModule">Invoicing & Billing Module</Label>
                      <p className="text-xs text-muted-foreground">Allows tenant to invoice patients and manage collections</p>
                    </div>
                    <Switch 
                      id="billingModule" 
                      checked={formData.features.billingModule}
                      onCheckedChange={(val) => handleInputChange('features.billingModule', val)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-xl bg-card">
                    <div className="space-y-0.5">
                      <Label htmlFor="smsNotifications">SMS Reminder Service</Label>
                      <p className="text-xs text-muted-foreground">Send automated SMS notifications to staff & patients</p>
                    </div>
                    <Switch 
                      id="smsNotifications" 
                      checked={formData.features.smsNotifications}
                      onCheckedChange={(val) => handleInputChange('features.smsNotifications', val)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-xl bg-card">
                    <div className="space-y-0.5">
                      <Label htmlFor="aiScheduling">AI Roster Optimization</Label>
                      <p className="text-xs text-muted-foreground">Auto-assign shifts based on caregiver preferences & location</p>
                    </div>
                    <Switch 
                      id="aiScheduling" 
                      checked={formData.features.aiScheduling}
                      onCheckedChange={(val) => handleInputChange('features.aiScheduling', val)}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-xl bg-card">
                    <div className="space-y-0.5">
                      <Label htmlFor="customRoles">Custom Roles & Permissions</Label>
                      <p className="text-xs text-muted-foreground">Create bespoke organizational roles beyond default rules</p>
                    </div>
                    <Switch 
                      id="customRoles" 
                      checked={formData.features.customRoles}
                      onCheckedChange={(val) => handleInputChange('features.customRoles', val)}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2 p-3 border rounded-xl bg-card">
                    <Label htmlFor="mobileAccess">Mobile Application Access</Label>
                    <Select 
                      value={formData.features.mobileAccess} 
                      onValueChange={(val) => handleInputChange('features.mobileAccess', val)}
                    >
                      <SelectTrigger id="mobileAccess">
                        <SelectValue placeholder="Select Mobile Access" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="readonly">Read-only Schedule access</SelectItem>
                        <SelectItem value="full">Full Mobile access (Online Only)</SelectItem>
                        <SelectItem value="full_offline">Full Mobile access + Offline Sync</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 p-3 border rounded-xl bg-card">
                    <Label htmlFor="apiAccess">Platform API Access</Label>
                    <Select 
                      value={formData.features.apiAccess} 
                      onValueChange={(val) => handleInputChange('features.apiAccess', val)}
                    >
                      <SelectTrigger id="apiAccess">
                        <SelectValue placeholder="Select API Access" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No API integration access</SelectItem>
                        <SelectItem value="readonly">Read-only Integration access</SelectItem>
                        <SelectItem value="full">Full Read/Write Integration access</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-xl bg-card">
                    <div className="space-y-0.5">
                      <Label htmlFor="whiteLabelBranding">White-Label Branding</Label>
                      <p className="text-xs text-muted-foreground">Allow upload of custom domain name and platform logo</p>
                    </div>
                    <Switch 
                      id="whiteLabelBranding" 
                      checked={formData.features.whiteLabelBranding}
                      onCheckedChange={(val) => handleInputChange('features.whiteLabelBranding', val)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 4: SUPPORT & SLA */}
          <TabsContent value="support">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Customer Support SLA Tiers</CardTitle>
                <CardDescription>Support availability configuration for the billing tier</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="supportChannel">Dedicated Support Channel</Label>
                    <Select 
                      value={formData.support.channel} 
                      onValueChange={(val) => handleInputChange('support.channel', val)}
                    >
                      <SelectTrigger id="supportChannel">
                        <SelectValue placeholder="Select Support Channel" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Standard Email Support</SelectItem>
                        <SelectItem value="chat">Priority Live Chat Support</SelectItem>
                        <SelectItem value="dedicated">Dedicated CSM & Phone Support</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="slaHours">Target Response SLA (Hours)</Label>
                    <Input 
                      id="slaHours" 
                      type="number"
                      min="1"
                      value={formData.support.slaHours}
                      onChange={(e) => handleInputChange('support.slaHours', e.target.value === '' ? '' : (parseInt(e.target.value, 10) || 48))}
                      required
                    />
                    <p className="text-xs text-muted-foreground">e.g. 24 hours.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer Actions */}
        <div className="flex justify-end gap-2 border-t pt-4 mt-6">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/subscriptions/plans')}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={loading}
            className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5"
          >
            <Save2 size={16} />
            {loading ? 'Saving...' : 'Save Plan Tier'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PlanEdit;
